import {
  TextField as AriaTextField,
  type TextFieldProps as AriaTextFieldProps,
  type ValidationResult
} from "react-aria-components";
import { tv } from "tailwind-variants";
import { twMerge } from "tailwind-merge";
import { Description, FieldError, Input, fieldBorderStyles } from "./Field";
import { Label } from "./Label";
import { composeTailwindRenderProps, focusRing } from "./utils";

const inputStyles = tv({
  extend: focusRing,
  base: "border-2 rounded-md [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:m-0 [&::-webkit-inner-spin-button]:m-0 [&[type=number]]:[-moz-appearance:textfield]",
  variants: {
    isFocused: fieldBorderStyles.variants.isFocusWithin,
    isInvalid: fieldBorderStyles.variants.isInvalid,
    isDisabled: fieldBorderStyles.variants.isDisabled
  }
});

export interface TextFieldProps extends AriaTextFieldProps {
  label?: string;
  description?: string;
  errorMessage?: string | ((validation: ValidationResult) => string);
  "data-testid": string;
  placeholder?: string;
  type?: "text" | "email" | "password" | "tel" | "url" | "search" | "number";
  inputClassName?: string;
}

export function TextField({
  label,
  description,
  errorMessage,
  "data-testid": dataTestId,
  placeholder,
  isRequired,
  type = "text",
  inputClassName,
  ...props
}: TextFieldProps) {
  return (
    <AriaTextField
      {...props}
      {...(isRequired !== undefined && { isRequired })}
      className={composeTailwindRenderProps(props.className, "flex flex-col gap-1")}
    >
      {label && (
        <Label data-testid="label">
          {label}
          {isRequired && <sup className="ml-0.5 text-red-500">*</sup>}
        </Label>
      )}
      <Input
        className={renderProps => twMerge(inputStyles(renderProps), inputClassName)}
        data-testid={dataTestId}
        placeholder={placeholder}
        type={type}
      />
      {description && <Description>{description}</Description>}
      <FieldError>{errorMessage}</FieldError>
    </AriaTextField>
  );
}
